import { DOMNode, domToReact, HTMLReactParserOptions, Text, Element } from 'html-react-parser';

import { Phone } from '~components/AllianceCallService/AllianceCallService';
import Anchor from '~components/Anchor/Anchor';
import ContentImage from '~components/ContentImage/ContentImage';
import constants from '~globals/constants';
import { IExtendedDOMNode, IExtendedParserOptions } from '~globals/interfaces';

import DynamicContentAnchor from '../DynamicContent/Anchor';
import DynamicContentParagraph from '../DynamicContent/Paragraph';

import wpAdminClasses from './wpAdminClasses';

export const getText = (n: IExtendedDOMNode): string =>
    n.type === 'text'
        ? (n as Text).data
        : (n as Element).children.map((e) => getText(e as IExtendedDOMNode)).join('');

const mergeClasses = (n: IExtendedDOMNode, c = [] as Array<string | boolean>) =>
    wpAdminClasses([...(n.attribs.class || '').split(' '), ...c].join(' ').trim());

const cssToObj = (css: string) => {
    const obj = {} as { [key: string]: string };
    const s = css
        .toLowerCase()
        .replace(/-(.)/g, function (m, g: string) {
            return g.toUpperCase();
        })
        .replace(/;\s?$/g, '')
        .split(/:|;/g);
    for (let i = 0; i < s.length; i += 2)
        obj[s[i].replace(/\s/g, '')] = s[i + 1].replace(/^\s+|\s+$/g, '');
    return obj;
};

const filteredNodes = (elements: JSX.Element[]) => {
    const isArray = Array.isArray(elements);

    if (!isArray) return elements;

    return elements.map((node) => {
        const isPhone = node.props?.href?.includes(constants.DEFAULT_PHONE.toString());

        if (typeof node === 'string') {
            return node;
        } else if (isPhone) {
            return <Phone key={node.key} classname="whitespace-nowrap" islink />;
        }

        return node;
    });
};

const postContentTag = (
    node: IExtendedDOMNode,
    options: IExtendedParserOptions,
    isNtmPage = false
) => {
    const { name, children, next, prev, endIndex, startIndex, type, parent, attribs, ...props } =
        node;
    const domChildren = domToReact(children as DOMNode[], options as HTMLReactParserOptions);
    const optionsLinkOnly: IExtendedParserOptions = {
        replace: (n: IExtendedDOMNode) => {
            if (n.type === 'text') {
                return n as Text;
            } else if (n.name === 'a') {
                return (
                    <Anchor {...n.attribs} className={mergeClasses(n, ['underline'])}>
                        {getText(n)}
                    </Anchor>
                );
            } else {
                return (
                    <>
                        {domToReact(
                            n.children as DOMNode[],
                            optionsLinkOnly as HTMLReactParserOptions
                        )}
                    </>
                );
            }
        },
    };

    if (attribs) {
        if (attribs.class == 'wp-block-spacer') {
            return;
        }

        if (attribs.style && typeof attribs.style === 'string') {
            attribs.style = cssToObj(attribs.style);
        }
    }

    const { style, onclick, ...filteredAttribs } = attribs;
    delete filteredAttribs.class;
    const hasDefaultPhone = filteredAttribs.href?.includes(constants.DEFAULT_PHONE.toString());
    const hasAllianceID = filteredAttribs.href?.includes('{{ALLIANCE_ID}}');
    const isStateLink = filteredAttribs.href?.includes('health-plans/group-medical');

    switch (name) {
        case 'a': {
            if (hasDefaultPhone) {
                return <Phone classname="whitespace-nowrap" islink />;
            } else if (isStateLink) {
                const absUrl = `${process.env.GATSBY_SITE_URL}${filteredAttribs.href}`;
                return (
                    <Anchor
                        className={mergeClasses(node, ['text-link', 'underline'])}
                        {...props}
                        {...filteredAttribs}
                        href={absUrl}
                    >
                        {domChildren}
                    </Anchor>
                );
            } else if (hasAllianceID) {
                return (
                    <DynamicContentAnchor
                        className={mergeClasses(node, ['text-link', 'underline'])}
                        {...props}
                        {...filteredAttribs}
                    >
                        {domChildren}
                    </DynamicContentAnchor>
                );
            }
            return (
                <Anchor
                    className={mergeClasses(node, ['text-link', 'underline'])}
                    {...props}
                    {...filteredAttribs}
                >
                    {domChildren}
                </Anchor>
            );
        }
        // case 'abbr':
        // case 'address':
        // case 'area':
        case 'article':
            return <article {...props}>{domChildren}</article>;
        // case 'aside':
        // case 'audio':
        case 'b':
            return (
                <span className="font-bold" {...props} {...filteredAttribs}>
                    {domChildren}
                </span>
            );
        // case 'base':
        // case 'bdi':
        // case 'bdo':
        // case 'blockquote':
        // case 'body':
        // case 'br':
        case 'button':
            return (
                <button className={mergeClasses(node, [''])} {...props} {...filteredAttribs}>
                    {domChildren}
                </button>
            );
        // case 'canvas':
        // case 'caption':
        // case 'cite':
        // case 'code':
        // case 'col':
        // case 'colgroup':
        // case 'data':
        // case 'datalist':
        // case 'dd':
        // case 'del':
        // case 'details':
        // case 'dfn':
        // case 'dialog':
        // case 'div':
        // case 'dl':
        // case 'dt':
        case 'em':
            return (
                <span className="italic" {...props}>
                    {domChildren}
                </span>
            );
        // case 'embed':
        // case 'fieldset':
        case 'figure-OLD':
            return (
                <figure
                    className={mergeClasses(node, ['rounded-lg', 'overflow-hidden'])}
                    {...props}
                >
                    {domChildren}
                </figure>
            );
        // domToReact does not seem to be parsing the children of `figure` tag correctly thus overriding the behavior
        case 'figure':
            return (
                <>
                    <figure className={mergeClasses(node, ['rounded-lg', 'overflow-hidden'])} {...props} {...filteredAttribs}>
                        {children
                            .filter(element => element.name === 'img')
                            .map((imgResource: IExtendedDOMNode, key: number) => (
                                <ContentImage
                                    src={
                                        decodeURIComponent(
                                            imgResource.attribs.src.includes('?u=')
                                                ? imgResource.attribs.src.split('?u=')[1]
                                                : imgResource.attribs.src
                                        ).split('&')[0]
                                    }
                                    className={mergeClasses(node, ['rounded-lg', 'overflow-hidden'])}
                                    alt={imgResource.attribs.alt || ''}
                                    key={key}
                                />
                            ))}
                        {children
                            .filter(element => element.name === 'figcaption')
                            .slice(0, 1)
                            .map((captionResource, key) => (
                                <figcaption key={key}>
                                    {captionResource.children.map((child: IExtendedDOMNode, childKey: number) =>
                                        child.type === 'text' ? (
                                            <span key={childKey}>{child.data}</span>
                                        ) : (
                                            child.children.map((grandChild: IExtendedDOMNode, grandChildKey: number) => (
                                                <span
                                                    key={grandChildKey}
                                                    className={classNames([
                                                        child.name === 'strong' && 'font-bold',
                                                        child.name === 'em' && 'italic',
                                                    ])}
                                                >
                                                    {grandChild.data}
                                                </span>
                                            ))
                                        )
                                    )}
                                </figcaption>
                            ))}
                    </figure>
                    <figure className={mergeClasses(node, ['rounded-lg', 'overflow-hidden'])} {...props} {...filteredAttribs}>
                        {domChildren}
                    </figure>
                </>
            );
        // case 'footer':
        // case 'form':
        case 'h1':
            return (
                <h1
                    className={mergeClasses(node, [
                        'text-32',
                        'leading-40',
                        'mb-3',
                        'md:text-32',
                        'md:leading-10',
                        'lg:text-48',
                        'lg:leading-64',
                        'font-poppins',
                        'font-bold',
                        'text-primary-dark',
                    ])}
                    {...props}
                    {...filteredAttribs}
                >
                    {!node.attribs?.class?.includes('wp-block-heading')
                        ? getText(node)
                        : domChildren}
                </h1>
            );
        case 'h2':
            return (
                <h2
                    id={`h-${getText(node).replace(/\s/g, '-').toLowerCase()}`}
                    className={mergeClasses(node, [
                        'text-default',
                        'mb-6',
                        'lg:mb-12',
                        'font-poppins',
                        'font-bold',
                        !isNtmPage
                            ? 'text-24 leading-32 md:text-32 md:leading-40 lg:leading-56'
                            : 'text-24 leading-32',
                        'text-primary-dark',
                    ])}
                    {...props}
                    {...filteredAttribs}
                >
                    {domToReact(children as DOMNode[], optionsLinkOnly as HTMLReactParserOptions)}
                </h2>
            );
        case 'h3':
            return (
                <h3
                    className={mergeClasses(node, [
                        'text-2xl',
                        'font-bold',
                        node.attribs?.class?.includes('wp-block-heading') ? 'bg-white' : '',
                        'mb-6',
                        'md:mb-8',
                        'font-poppins',
                        'leading-32 md:leading-40',
                        'text-primary-dark',
                    ])}
                    {...props}
                    {...filteredAttribs}
                >
                    {getText(node)}
                </h3>
            );
        case 'h4': {
            const elements = domToReact(
                children as DOMNode[],
                optionsLinkOnly as HTMLReactParserOptions
            );
            const filteredEls = filteredNodes(elements);

            return (
                <h4
                    className={mergeClasses(node, [
                        'text-16',
                        'leading-24',
                        'lg:text-18',
                        'lg:leading-28',
                        'font-bold',
                        'font-poppins',
                        'text-primary-dark',
                    ])}
                    {...props}
                    {...filteredAttribs}
                >
                    {filteredEls}
                </h4>
            );
        }
        case 'h5':
            return (
                <h5
                    className={mergeClasses(node, ['text-default', 'font-poppins', 'font-bold'])}
                    {...props}
                    {...filteredAttribs}
                >
                    {getText(node)}
                </h5>
            );
        case 'h6':
            return (
                <h6 className={mergeClasses(node, ['text-default', 'font-poppins', 'font-bold'])}>
                    {getText(node)}
                </h6>
            );
        // case 'head':
        // case 'header':
        // case 'hgroup':
        case 'hr':
            return <hr className="mb-4 border-2" />;
        // case 'html':
        // case 'i':
        // case 'iframe':
        case 'img':
            delete attribs.class;
            // eslint-disable-next-line no-case-declarations
            const urlArr = attribs['data-src']?.split('?u=') ?? [];
            // eslint-disable-next-line no-case-declarations
            const url = decodeURIComponent(urlArr[1]).split('&')[0];
            return <ContentImage src={url} alt={attribs.alt || ''} loading='lazy' />;
        // case 'input':
        // case 'ins':
        // case 'kbd':
        // case 'keygen':
        // case 'label':
        // case 'legend':
        case 'li':
            return (
                <li className={mergeClasses(node, [''])} {...props}>
                    {domChildren}
                </li>
            );
        // case 'link':
        // case 'main':
        // case 'map':
        case 'mark':
            return (
                <span className={mergeClasses(node, ['text-partner-branded'])} {...filteredAttribs}>
                    {domChildren}
                </span>
            );
        // case 'menu':
        // case 'menuitem':
        // case 'meta':
        // case 'meter':
        // case 'nav':
        // case 'noscript':
        // case 'object':
        case 'ol':
            return <ol className="list-decimal ml-4">{domChildren}</ol>;
        // case 'optgroup':
        // case 'option':
        // case 'output':
        case 'p': {
            const className = mergeClasses(node, [
                !/text-sm|text-lg|text-xl|text-2xl|text-3xl|text-4xl|text-5xl/.test(
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    !node.attribs?.class
                ) && 'font-sans text-base',
                !node.attribs?.class?.includes('mb-') && 'mb-4 md:mb-8',
                'print:break-inside-avoid',
                'text-primary-dark',
            ]);
            if (
                ['{{PHONE_NUMBER}}', '{{HOURS}}'].some((variable) =>
                    getText(node).includes(variable)
                )
            ) {
                return (
                    <DynamicContentParagraph className={className} {...props}>
                        {domChildren}
                    </DynamicContentParagraph>
                );
            }
            return (
                <p className={className} {...props}>
                    {domChildren}
                </p>
            );
        }
        // case 'param':
        // case 'pre':
        // case 'progress':
        // case 'q':
        // case 'rb':
        // case 'rp':
        // case 'rt':
        // case 'rtc':
        // case 'ruby':
        // case 's':
        // case 'samp':
        // case 'script':
        // case 'section':
        // case 'select':
        // case 'small':
        // case 'source':
        case 'span':
            return <span className={mergeClasses(node, [''])}>{domChildren}</span>;
        case 'strong':
            return (
                <span className="font-bold" {...props}>
                    {domChildren}
                </span>
            );
        // case 'style':
        // case 'sub':
        // case 'summary':
        case 'sup':
            return <sup {...props}>{domChildren}</sup>;
        case 'table':
            return (
                <div className="w-full overflow-y-hidden overflow-x-auto min-h-1 mb-8">
                    <table className={mergeClasses(node, ['w-full'])} {...props}>
                        {domChildren}
                    </table>
                </div>
            );
        // case 'tbody':
        case 'td':
            return (
                <td className={mergeClasses(node, ['p-1'])} {...props}>
                    {domChildren}
                </td>
            );
        // case 'template':
        // case 'textarea':
        // case 'tfoot':
        case 'th':
            return (
                <th
                    className={mergeClasses(node, [
                        'text-center',
                        'font-bold',
                        'p-1',
                        'border-b-black border-b ',
                    ])}
                    {...props}
                >
                    {domChildren}
                </th>
            );
        // case 'thead':
        // case 'time':
        // case 'title':
        case 'tr':
            return (
                <tr
                    className={mergeClasses(node, [
                        'border-dotted border-b border-gray last:border-b-0',
                    ])}
                    {...props}
                >
                    {domChildren}
                </tr>
            );
        // case 'track':
        case 'u':
            return (
                <span className="text-underline" {...props}>
                    {domChildren}
                </span>
            );
        case 'ul':
            return (
                <ul
                    className={mergeClasses(node, [
                        !node.attribs?.class?.includes('list-sneaky') &&
                            'list-disc list-outside ml-8 mb-4 md:mb-8',
                    ])}
                    {...props}
                >
                    {domChildren}
                </ul>
            );
        // case 'var':
        // case 'video':
        // case 'wbr':
        default:
            return node;
    }
};

export default postContentTag;
